import {
  Button,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Text
} from "@chakra-ui/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FiZoomIn } from "react-icons/fi";

const VIEWPORT = 300; // px — kwadratowy podgląd
const OUTPUT = 512; // px — wynikowy kwadrat (JPEG)

type Props = {
  file: File | null;
  isOpen: boolean;
  onClose: () => void;
  onCropped: (file: File) => void;
  title?: string;
};

// Interaktywny cropper kwadratowy (pan + zoom). Wynik: 512×512 JPEG (canvas re-enkoduje → strzyże EXIF).
const ImageCropModal: React.FC<Props> = ({ file, isOpen, onClose, onCropped, title = "Przytnij obraz" }) => {
  const imgRef = useRef<HTMLImageElement | null>(null);
  const [src, setSrc] = useState<string>("");
  const [nat, setNat] = useState({ w: 0, h: 0 });
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const drag = useRef<{ x: number; y: number } | null>(null);
  const [busy, setBusy] = useState(false);

  const baseScale = nat.w && nat.h ? Math.max(VIEWPORT / nat.w, VIEWPORT / nat.h) : 1;
  const scale = baseScale * zoom;
  const dw = nat.w * scale;
  const dh = nat.h * scale;

  // Trzymaj obraz tak, by ZAWSZE pokrywał kwadrat (brak pustych marginesów).
  const clamp = useCallback(
    (o: { x: number; y: number }) => ({
      x: Math.min(0, Math.max(VIEWPORT - dw, o.x)),
      y: Math.min(0, Math.max(VIEWPORT - dh, o.y))
    }),
    [dw, dh]
  );

  useEffect(() => {
    if (!file) return;
    const url = URL.createObjectURL(file);
    setSrc(url);
    setZoom(1);
    setOffset({ x: 0, y: 0 });
    return () => URL.revokeObjectURL(url);
  }, [file]);

  // Po wczytaniu / zmianie zoomu — wyśrodkuj i doklamuj.
  useEffect(() => {
    if (!nat.w) return;
    setOffset(o => clamp({ x: o.x || (VIEWPORT - dw) / 2, y: o.y || (VIEWPORT - dh) / 2 }));
  }, [nat.w, nat.h, zoom, dw, dh, clamp]);

  const onImgLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const el = e.currentTarget;
    setNat({ w: el.naturalWidth, h: el.naturalHeight });
    setOffset({ x: (VIEWPORT - el.naturalWidth * Math.max(VIEWPORT / el.naturalWidth, VIEWPORT / el.naturalHeight)) / 2, y: (VIEWPORT - el.naturalHeight * Math.max(VIEWPORT / el.naturalWidth, VIEWPORT / el.naturalHeight)) / 2 });
  };

  const onPointerDown = (e: React.PointerEvent) => {
    drag.current = { x: e.clientX - offset.x, y: e.clientY - offset.y };
    (e.target as Element).setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e: React.PointerEvent) => {
    if (!drag.current) return;
    setOffset(clamp({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y }));
  };
  const onPointerUp = () => {
    drag.current = null;
  };

  const confirm = async () => {
    if (!imgRef.current || !nat.w) return;
    setBusy(true);
    try {
      const canvas = document.createElement("canvas");
      canvas.width = OUTPUT;
      canvas.height = OUTPUT;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("no ctx");
      // Region źródłowy widoczny w kwadracie → rysuj do 512×512.
      const sx = -offset.x / scale;
      const sy = -offset.y / scale;
      const sSize = VIEWPORT / scale;
      ctx.drawImage(imgRef.current, sx, sy, sSize, sSize, 0, 0, OUTPUT, OUTPUT);
      const blob: Blob | null = await new Promise(res => canvas.toBlob(res, "image/jpeg", 0.85));
      if (!blob) throw new Error("no blob");
      onCropped(new File([blob], "avatar.jpg", { type: "image/jpeg" }));
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm">
      <ModalOverlay />
      <ModalContent borderRadius="18px">
        <ModalHeader color="primary" fontWeight="800" fontSize="17px">{title}</ModalHeader>
        <ModalBody>
          <Flex direction="column" align="center" gap="16px">
            <div
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerLeave={onPointerUp}
              style={{
                position: "relative",
                width: VIEWPORT,
                height: VIEWPORT,
                borderRadius: "50%",
                overflow: "hidden",
                background: "#edf2f7",
                cursor: "grab",
                touchAction: "none",
                boxShadow: "inset 0 0 0 2px rgba(67,0,152,0.15)"
              }}
            >
              {src && (
                <img
                  ref={imgRef}
                  src={src}
                  alt=""
                  onLoad={onImgLoad}
                  draggable={false}
                  style={{
                    position: "absolute",
                    left: 0,
                    top: 0,
                    width: `${dw}px`,
                    height: `${dh}px`,
                    transform: `translate(${offset.x}px, ${offset.y}px)`,
                    maxWidth: "none",
                    userSelect: "none"
                  }}
                />
              )}
            </div>
            <HStack w="100%" spacing="10px" px="4px">
              <Icon as={FiZoomIn} color="gray.400" />
              <Slider aria-label="zoom" min={1} max={3} step={0.01} value={zoom} onChange={setZoom} focusThumbOnChange={false}>
                <SliderTrack bg="rgba(67,0,152,0.12)"><SliderFilledTrack bg="primary" /></SliderTrack>
                <SliderThumb boxSize="16px" />
              </Slider>
            </HStack>
            <Text fontSize="12px" color="gray.400">Przeciągnij, aby przesunąć · suwak przybliża</Text>
          </Flex>
        </ModalBody>
        <ModalFooter gap="10px">
          <Button variant="ghost" onClick={onClose} borderRadius="11px">Anuluj</Button>
          <Button bg="primary" color="white" onClick={confirm} isLoading={busy} borderRadius="11px" _hover={{ bg: "#36007a" }}>
            Zapisz
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImageCropModal;
